import {
  CYBERSCORE_ALGORITHM,
  THREATS,
  ownerNextSteps,
  type ChangePart,
  type InsightTarget,
  type Threat,
} from '@cyberscore/contracts';
import {
  Activity,
  ArrowRight,
  ArrowUpRight,
  Award,
  BadgeCheck,
  ChevronRight,
  ClipboardCheck,
  Cloud,
  FileCheck2,
  FileLock2,
  Gauge as GaugeIcon,
  Globe,
  LayoutGrid,
  ListChecks,
  Info,
  Lock,
  Mail,
  Pencil,
  MessagesSquare,
  Plug,
  Rocket,
  Route as RouteIcon,
  Share2,
  ShieldHalf,
  Sparkles,
  Target,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { href } from '../../lib/router';
import { useSession } from '../../lib/session';
import { industryLabel } from '../assessments/AssessmentsPage';
import { useScoreChange, type StoredScoreRow } from '../portfolio/api';
import { bandLabel } from '../portfolio/format';
import { BAND_COLOR, Caps, Delta, Gauge, LineChart, Ring, resTone } from './charts';
import { useBusiness, useBusinessScore } from './context';
import { CardHead, MissionIcon, PtsChip, controlLink, shortDate } from './parts';
import { LEVELS, VERIFIED_AT, levelOf, milestones } from './progress';
import { ShareScore } from './ShareScore';
import { ChangesForYou } from '../review/ChangesForYou';
import { useChangeRequests } from '../review/api';
import { FollowUpsForYou } from '../follow-ups/FollowUpsForYou';
import { Conditions } from '../decisions/Conditions';
import { MessagesLink } from '../messages/MessagesLink';
import { NoAssessment, ScoreProblem } from './states';
import { useAssessmentSignals } from '../record/signals';
import {
  NextStep,
  RecordBody,
  RecordFrame,
  RecordHero,
  useRecordTab,
  type FolderTab,
  type Insight,
} from '../../ui/record/Record';

/** Home's folder tabs (task 101): the long page in three parts, the overview open on arrival. */
const TABS: FolderTab[] = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'missions', label: 'Missions', icon: Target },
  { id: 'history', label: 'Score history', icon: Activity },
];

const INSIGHT_ICON: Record<string, LucideIcon> = {
  changes: Pencil,
  check: ClipboardCheck,
  submit: FileCheck2,
  cap: Lock,
  'follow-up': MessagesSquare,
  conflict: Globe,
  mission: Target,
};

const THREAT_ICON: Record<Threat, LucideIcon> = {
  ransomware: Lock,
  breach: FileLock2,
  bec: Mail,
  cloud: Cloud,
  third: Truck,
};

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

/**
 * The business owner's home (the prototype's CyberCheck home): the score on a gauge with the next fixes
 * marked on it, resilience against the losses insurers see most, the journey, missions, and what moved
 * the score. Every number comes from the stored score and its trace. It sits in the record layout (task 101,
 * decision 0073): the business in the header with what to do next, and the page in folder tabs.
 */
export function HomePage() {
  const { me } = useSession();
  const { loading } = useBusiness();
  const { current: a, view, derived } = useBusinessScore();
  const [sharing, setSharing] = useState(false);
  const { signals } = useAssessmentSignals(a?.id ?? null);
  const changes = useChangeRequests(a?.id ?? '', a?.status === 'in_remediation');
  const { active, select } = useRecordTab(TABS);
  if (loading) return <div className="empty">Loading…</div>;
  if (!a) return <NoAssessment />;
  if (view.isPending) return <div className="empty">Loading…</div>;
  if (view.isError || !derived) return <ScoreProblem a={a} error={view.error} />;

  const { score: r, explanation: trace, history } = view.data;
  const { missions, plan, next, requirements } = derived;
  const together = next.points;
  const target = next.score;
  const confidence = Math.round(r.controls.evidence_confidence * 100);
  const verified = r.controls.evidence_confidence >= VERIFIED_AT;
  // Movement counts from the first complete score: provisional ones only show the answers filling in.
  const full = history.filter((h) => !h.provisional);
  const oldest = full.length > 1 ? full[full.length - 1] : undefined;
  const open = missions.filter((m) => m.next.steps.length > 0);
  const lvl = levelOf({ provisional: r.provisional, confidence: r.controls.evidence_confidence, requirements, submitted: a.status !== 'in_progress' }); // prettier-ignore
  const firstName = (me?.user.name ?? '').split(' ')[0];
  const chat = a.status === 'in_progress';

  const to = (t: InsightTarget): string => {
    switch (t.to) {
      case 'mission':
        return href.missions(t.mission);
      case 'answer':
      case 'conflicts':
        return t.to === 'answer' && t.question ? href.answer(a.id, t.question) : href.assessment(a.id);
      case 'documents':
        return href.locker();
      case 'follow_ups':
        return href.assessment(a.id);
      case 'score':
      case 'outside_in':
        return href.score(a.id);
    }
  };
  const asked = changes.data?.requests.filter((q) => q.state === 'sent') ?? [];
  const steps: Insight[] = ownerNextSteps(
    { ...signals, changes: { asked: asked.length, left: asked.filter((q) => !q.addressed).length } },
    a.status,
  ).map((x) => ({
    id: x.id,
    icon: INSIGHT_ICON[x.id.split(':')[0]!] ?? ListChecks,
    title: x.title,
    text: x.text,
    tone: x.tone,
    source: { label: x.source.label, href: to(x.source.target) },
    action: { label: x.action.label, href: to(x.action.target) },
  }));

  return (
    <>
      <RecordFrame className="biz-record">
        <RecordHero
          crumbs={[{ label: `${greeting()}${firstName ? `, ${firstName}` : ''}` }]}
          title={a.customer_name}
          pill={<span className="bbadge" style={{ color: BAND_COLOR[r.band] }}>{bandLabel(r.band)} · {r.score}</span>}
          subline={[industryLabel(a.context.industry), ...(history[0] ? [`Updated ${shortDate(history[0].created_at)}`] : []), verified ? 'Verified' : 'Mostly self-declared']}
          actions={
            <div className="head-actions">
              <a className="bbtn" href={href.score(a.id)}>
                <GaugeIcon size={15} /> How it’s worked out
              </a>
              <MessagesLink id={a.id} className="bbtn" />
              <button type="button" className="bbtn" onClick={() => setSharing(true)}>
                <Share2 size={15} /> Share my score
              </button>
            </div>
          }
          tabs={TABS}
          active={active}
          onSelect={select}
          tabsLabel="Your business"
        >
          <NextStep
            steps={steps}
            done={{
              title: 'You’re all set',
              text: 'Nothing open and nothing left that adds points. Keep your evidence current, and share your score with customers and insurers.',
              action: { label: 'Share your score', onClick: () => setSharing(true) },
            }}
          />
        </RecordHero>
        <RecordBody>
          {active === 'overview' && (
            <>
              <ChangesForYou id={a.id} status={a.status} />
              <FollowUpsForYou id={a.id} status={a.status} />
              {a.status === 'decided' && <Conditions id={a.id} mode="business" />}
              <div className="bgrid g-home">
                <section className="bcard score-card">
                  <CardHead
                    icon={<GaugeIcon size={18} />}
                    title="CyberScore"
                    sub={`Model ${CYBERSCORE_ALGORITHM.replace('cyberscore/', '')}${history[0] ? ` · updated ${shortDate(history[0].created_at)}` : ''}`}
                    end={<span className={`bbadge ${verified ? 'ok' : 'warn'}`}>{verified ? 'Verified' : 'Mostly self-declared'}</span>}
                  />
                  <div className="gauge-wrap">
                    <Gauge score={r.score} band={r.band} target={target} />
                    <div className="gauge-center">
                      <div className="big num">{r.score}</div>
                      <div className="band" style={{ color: BAND_COLOR[r.band] }}>
                        {bandLabel(r.band).toUpperCase()}
                      </div>
                      <div className="of">out of 1000</div>
                    </div>
                  </div>
                  <div className="score-meta">
                    {oldest ? (
                      <>
                        <Delta n={r.score - oldest.score} /> <span className="muted small">since {shortDate(oldest.created_at)}</span>
                      </>
                    ) : r.provisional ? (
                      <span className="muted small">Provisional: some required questions are still open</span>
                    ) : (
                      <span className="muted small">Your first score</span>
                    )}
                  </div>
                  {r.kill_switches.cap !== null && r.uncapped > r.kill_switches.cap && (
                    <p className="peer">
                      Held at <b>{r.kill_switches.cap}</b> by {r.kill_switches.failed.length === 1 ? 'a missing protection' : `${r.kill_switches.failed.length} missing protections`} insurers insist on.
                    </p>
                  )}
                  <hr className="divider" />
                  <div className="conf">
                    <Ring value={confidence} color={verified ? 'var(--ok-strong)' : 'var(--warn-strong)'} />
                    <div className="grow">
                      <b>Score confidence</b>
                      <p className="muted small">
                        {verified
                          ? 'Most of your answers are backed by documents or connected systems.'
                          : 'Most answers are self-declared. Connect Microsoft 365 or upload documents to verify them.'}
                      </p>
                      <a className="bbtn sm soft mt-s" href={verified ? href.locker() : href.connections()}>
                        {verified ? <>See evidence <ArrowRight size={14} /></> : <><Plug size={14} /> Connect your systems</>}
                      </a>
                    </div>
                  </div>
                </section>

                <section className="bcard">
                  <CardHead
                    tone="acc"
                    icon={<Rocket size={18} />}
                    title={`Your next ${next.steps.length || ''} fixes`.replace('  ', ' ')}
                    sub={next.steps.some((s) => s.lifts_cap) ? 'Starting with what holds your score down' : 'The most points for the effort'}
                    end={next.steps.length > 0 && <div className="together"><b className="num">+{together}</b><span>together</span></div>}
                  />
                  {next.steps.length ? (
                    <div className="stack-s">
                      {next.steps.map((s) => {
                        const link = controlLink(a, trace, s.control, s.mission);
                        const m = missions.find((x) => x.id === s.mission);
                        return (
                          <div key={s.control} className="action">
                            <div className="pts">+{s.points}</div>
                            <div className="grow">
                              <div className="t">{s.name}</div>
                              <div className="m">
                                {m && <span><MissionIcon id={m.id} size={13} />{m.name}</span>}
                                {s.lifts_cap && <span className="bad-t">Lifts the cap</span>}
                              </div>
                            </div>
                            <a className={`bbtn sm${link.label === 'See mission' ? '' : ' pri'}`} href={link.to}>
                              {link.label}
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="bnote ok"><BadgeCheck size={15} /><span>Nothing left that would add points. Keep your evidence current.</span></div>
                  )}
                  <div className="bnote mt">
                    <Info size={15} />
                    <span>
                      One fix moves your score a few points. Done together, everything open adds about <b className="num">{plan.all.points}</b>. Points come from the CyberScore model and are confirmed once your answers are verified.
                    </span>
                  </div>
                </section>

                <section className="bcard">
                  <CardHead icon={<ShieldHalf size={18} />} title="How resilient you are" sub="Against the losses insurers see most" />
                  <div className="stack-s res-list">
                    {(Object.keys(THREATS) as Threat[]).map((t) => {
                      const I = THREAT_ICON[t];
                      return (
                        <div key={t}>
                          <div className="row-between small">
                            <span className="row gap-s"><I size={14} />{THREATS[t].name}</span>
                            <b className="num">{Math.round(r.resilience[t])}</b>
                          </div>
                          <div className="mt-s"><Caps value={r.resilience[t]} n={22} tone={resTone(r.resilience[t])} /></div>
                        </div>
                      );
                    })}
                  </div>
                  <a className="link-btn mt" href={href.missions()}>
                    Improve these in Missions <ArrowRight size={14} />
                  </a>
                </section>

                <section className="bcard span-all">
                  <CardHead
                    tone="ok"
                    icon={<RouteIcon size={18} />}
                    title="Your journey"
                    sub="Go as far as your business needs. A small shop may stop at Verified; a business buying a large policy goes further."
                    end={<span className="bbadge pri">Level {Math.min(lvl + 1, LEVELS.length)} of {LEVELS.length} · {LEVELS[Math.min(lvl, LEVELS.length - 1)]!.name}</span>}
                  />
                  <ol className="journey">
                    {LEVELS.map((l, i) => (
                      <li key={l.key} className={i < lvl ? 'done' : i === lvl ? 'cur' : ''}>
                        <span className="dot">{i < lvl ? <BadgeCheck size={14} /> : i + 1}</span>
                        <b>{l.name}</b>
                        <span>{i === lvl ? levelHint(l.key, { r, requirements, confidence }) : l.blurb}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              </div>
            </>
          )}
          {active === 'missions' && (
            <div className="bgrid g-2">
              <section className="bcard">
                <CardHead
                  icon={<Target size={18} />}
                  title="Missions"
                  sub="Behind each mission are NIST CSF 2.0 and CIS controls. You only see what to do."
                  end={<a className="bbtn sm" href={href.missions()}>All missions</a>}
                />
                <div>
                  {(open.length ? open : missions).slice(0, 5).map((m) => {
                    const pct = Math.round((100 * m.in_place) / m.controls);
                    return (
                      <a key={m.id} className="mission-row" href={href.missions(m.id)}>
                        <span className="hicon">
                          <MissionIcon id={m.id} />
                        </span>
                        <span className="grow">
                          <span className="row-between">
                            <b>{m.name}</b>
                            <span className="num strong">{pct}%</span>
                          </span>
                          <span className={`bar thin ${pct >= 75 ? 'ok' : pct < 45 ? 'warn' : ''}`}><span style={{ width: `${pct}%` }} /></span>
                          <span className="row-between small">
                            <span className="muted">{m.in_place} of {m.controls} protections in place</span>
                            <span className="ok-t num strong">{m.points ? `+${m.points} pts available` : m.after_cap ? `+${m.after_cap} once the cap is lifted` : ''}</span>
                          </span>
                        </span>
                        <ChevronRight size={15} className="muted" />
                      </a>
                    );
                  })}
                </div>
              </section>
              <section className="bcard">
                <CardHead icon={<Award size={18} />} title="Milestones" sub="Professional milestones you can share with customers, suppliers and insurers" />
                <div className="ach-grid">
                  {milestones(missions, r.controls.evidence_confidence, requirements).slice(0, 8).map((m) => (
                    <div key={m.key} className={`ach ${m.earned ? 'got' : 'locked'}`}>
                      <div className="medal">{m.earned ? <BadgeCheck size={18} /> : <Lock size={14} />}</div>
                      <div>
                        <b>{m.name}</b>
                        <span>{m.earned ? 'Earned' : m.blurb}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
          {active === 'history' && (
            <div className="bgrid g-2">
              <History id={a.id} history={history} />
              <section className="promo">
                <div className="row gap-s">
                  <span className="pill-ic"><Sparkles size={16} /></span>
                  <b>Cyber assistant</b>
                </div>
                <h3>Ask anything about your score</h3>
                {chat ? (
                  <>
                    <ul>
                      {['Why is my score what it is?', 'What does my insurer need from me?', 'What should I fix first?'].map((q) => (
                        <li key={q}>
                          <ArrowUpRight size={14} />
                          <span>{q}</span>
                        </li>
                      ))}
                    </ul>
                    <a className="bbtn" href={href.chat(a.id)}>
                      <MessagesSquare size={15} /> Ask the assistant
                    </a>
                  </>
                ) : (
                  <p>The assistant answers while an assessment is open. This one is submitted, so its answers are fixed.</p>
                )}
              </section>
            </div>
          )}
        </RecordBody>
      </RecordFrame>
      {sharing && (
        <ShareScore
          a={a}
          score={{ score: r.score, band: r.band, verified: r.controls.evidence_confidence, provisional: r.provisional, date: full[0]?.created_at ?? null }} // prettier-ignore
          onClose={() => setSharing(false)}
        />
      )}
    </>
  );
} // prettier-ignore

function levelHint(key: string, s: { r: { controls: { applicable: number; answered: number } }; requirements: { state: string }[]; confidence: number }) {
  if (key === 'check') return `${s.r.controls.applicable - s.r.controls.answered} protections still to answer`;
  if (key === 'verified') return `${s.confidence}% verified · ${VERIFIED_AT * 100}% needed`;
  if (key === 'protected') return `${s.requirements.filter((q) => q.state !== 'met').length} insurer minimums to go`;
  if (key === 'insurable') return 'Submit to your insurer';
  return 'Independent review';
} // prettier-ignore

function partText(p: ChangePart) {
  if (p.kind === 'inherent') return { t: 'Your business profile changed', s: `Exposure ${p.before} → ${p.after}` };
  if (p.kind === 'reweighting') return { t: 'Other protections reweighted', s: `${p.controls} came in or left` };
  const why = { credit: 'Answer changed', evidence: 'Evidence changed', added: 'Now applies', removed: 'No longer applies' }[p.reason];
  return { t: p.name, s: why + (p.cap ? (p.cap.after === null ? ' · lifted the cap' : ' · capped the score') : '') };
} // prettier-ignore

function History({ id, history }: { id: string; history: StoredScoreRow[] }) {
  const latest = history[0] ?? null;
  const move = useScoreChange(id, history.length > 1 ? latest!.seq : null);
  const span = history.length > 1 ? Date.parse(history[0]!.created_at) - Date.parse(history[history.length - 1]!.created_at) : 0;
  const label = (iso: string) => (span < 2 * 86_400_000 ? new Date(iso).toLocaleTimeString('en-MY', { hour: 'numeric', minute: '2-digit' }) : shortDate(iso));
  const points = [...history].reverse().map((h) => ({ x: new Date(h.created_at).getTime(), y: h.score, label: label(h.created_at) }));
  const parts = move.data?.change?.parts.slice(0, 5) ?? [];
  return (
    <section className="bcard">
      <CardHead icon={<Activity size={18} />} title="What changed your score" sub={history.length > 1 ? `${history.length} scores since ${shortDate(history[history.length - 1]!.created_at)}` : 'Every change is recorded'} />
      {points.length > 1 ? <LineChart points={points} mark={{ y: 750, label: 'Very good · 750' }} /> : <p className="muted small">One score so far. The chart fills in as your answers change it.</p>}
      {parts.length > 0 && (
        <div className="feed mt">
          {parts.map((p, i) => {
            const x = partText(p);
            return (
              <div key={i} className="fi">
                <span className={`ico ${p.points > 0 ? 'ok' : p.points < 0 ? 'bad' : ''}`}>{p.points >= 0 ? <ArrowUpRight size={14} /> : <Activity size={14} />}</span>
                <div className="grow">
                  <div className="t">{x.t}</div>
                  <div className="s">{x.s}{move.data?.to ? ` · ${shortDate(move.data.to.created_at)}` : ''}</div>
                </div>
                <PtsChip n={p.points} />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
} // prettier-ignore
